import React, { useState, useEffect } from'react';
import { Trophy, Music, TrendingUp, Calendar, ExternalLink, Loader2 } from'lucide-react';
import { Button } from'./ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from'./ui/select';
import { Badge } from'./ui/badge';

import { API_URL } from'../utils/api';

const LEVEL_COLORS = {
'Absolute Beginner':'bg-green-500',
'Beginner':'bg-lime-500',
'Improver':'bg-yellow-500',
'Intermediate':'bg-orange-500',
'Advanced':'bg-red-500',
'Unknown':'bg-slate-500'
};

export default function TopRequestedDances() {
 const [data, setData] = useState(null);
 const [loading, setLoading] = useState(true);
 const [period, setPeriod] = useState('all');

 useEffect(() => {
 fetchTopDances();
 }, [period]);

 const fetchTopDances = async () => {
 setLoading(true);
 try {
 const token = localStorage.getItem('token');
 const response = await fetch(
 `${API_URL}/api/dj/analytics/top-requested?period=${period}&limit=10`,
 { headers: {'Authorization': `Bearer ${token}` } }
 );
 
 if (response.ok) {
 const result = await response.json();
 setData(result);
 }
 } catch (error) {
 console.error('Error fetching top dances:', error);
 } finally {
 setLoading(false);
 }
 };

 const getRankBadge = (index) => {
 if (index === 0) return'🥇';
 if (index === 1) return'🥈';
 if (index === 2) return'🥉';
 return `#${index + 1}`;
 };

 const formatDate = (dateStr) => {
 if (!dateStr) return'N/A';
 return new Date(dateStr).toLocaleDateString('en-GB', {
 day:'numeric',
 month:'short'
 });
 };

 return (
 <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
 {/* Header */}
 <div className="p-6 border-b border-slate-200">
 <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
 <div className="flex items-center gap-3">
 <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-yellow-400 to-amber-500 flex items-center justify-center">
 <Trophy className="w-6 h-6 text-white" />
 </div>
 <div>
 <h2 className="text-xl font-bold text-slate-900">Top Requested Dances</h2>
 <p className="text-sm text-slate-400">
 Most requested dances across all DJ sessions
 </p>
 </div>
 </div>
 
 <Select value={period} onValueChange={setPeriod}>
 <SelectTrigger className="w-40" data-testid="top-dances-period-select">
 <SelectValue placeholder="Time period" />
 </SelectTrigger>
 <SelectContent>
 <SelectItem value="week">Past Week</SelectItem>
 <SelectItem value="month">Past Month</SelectItem>
 <SelectItem value="all">All Time</SelectItem>
 </SelectContent>
 </Select>
 </div>
 
 {/* Summary Stats */}
 {data && (
 <div className="flex gap-6 mt-4 pt-4 border-t border-slate-100">
 <div className="flex items-center gap-2">
 <Calendar className="w-4 h-4 text-violet-500" />
 <span className="text-sm text-slate-600">
 <strong className="text-slate-900">{data.total_sessions}</strong> sessions
 </span>
 </div>
 <div className="flex items-center gap-2">
 <Music className="w-4 h-4 text-teal-500" />
 <span className="text-sm text-slate-600">
 <strong className="text-slate-900">{data.total_requests}</strong> total requests
 </span>
 </div>
 </div>
 )}
 </div>
 
 {/* Content */}
 <div className="p-6">
 {loading ? (
 <div className="flex items-center justify-center py-12">
 <Loader2 className="w-8 h-8 animate-spin text-violet-500" />
 </div>
 ) : !data || data.top_dances.length === 0 ? (
 <div className="text-center py-12">
 <Music className="w-16 h-16 text-slate-300 mx-auto mb-4" />
 <h3 className="text-lg font-semibold text-slate-700 mb-2">No requests yet</h3>
 <p className="text-slate-400">
 Dance requests from DJ sessions will appear here
 </p>
 </div>
 ) : (
 <div className="space-y-3">
 {data.top_dances.map((dance, index) => (
 <div 
 key={index}
 className={`flex items-center gap-4 p-4 rounded-xl transition-colors ${
 index < 3 
 ?'bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200' 
 :'bg-slate-50 border border-slate-100'
 }`}
 data-testid={`top-dance-${index}`}
 >
 {/* Rank */}
 <div className={`w-10 h-10 rounded-lg flex items-center justify-center font-bold text-lg ${
 index === 0 ?'bg-yellow-400 text-yellow-900' :
 index === 1 ?'bg-slate-300 text-slate-700' :
 index === 2 ?'bg-amber-600 text-white' :
'bg-slate-200 text-slate-600'
 }`}>
 {index < 3 ? getRankBadge(index) : index + 1}
 </div>
 
 {/* Dance Info */}
 <div className="flex-1 min-w-0">
 <div className="flex items-center gap-2">
 {dance.dance_url ? (
 <a 
 href={dance.dance_url}
 target="_blank"
 rel="noopener noreferrer"
 className="font-semibold text-slate-900 hover:text-violet-600 flex items-center gap-1"
 >
 {dance.dance_name}
 <ExternalLink className="w-3 h-3" />
 </a>
 ) : (
 <span className="font-semibold text-slate-900">{dance.dance_name}</span>
 )}
 {dance.level && (
 <Badge className={`${LEVEL_COLORS[dance.level] || LEVEL_COLORS['Unknown']} text-white text-xs`}>
 {dance.level}
 </Badge>
 )}
 </div>
 {dance.choreographer && (
 <p className="text-sm text-slate-400 truncate">
 by {dance.choreographer}
 </p>
 )}
 </div>
 
 {/* Stats */}
 <div className="flex items-center gap-6 text-sm">
 <div className="text-center">
 <p className="font-bold text-violet-600 text-lg">{dance.request_count}</p>
 <p className="text-slate-400 text-xs">requests</p>
 </div>
 <div className="text-center">
 <p className="font-bold text-green-600 text-lg">{dance.times_played}</p>
 <p className="text-slate-400 text-xs">played</p>
 </div>
 <div className="text-center hidden sm:block">
 <p className="font-medium text-slate-600">{formatDate(dance.last_requested)}</p>
 <p className="text-slate-400 text-xs">last requested</p>
 </div>
 </div>
 </div>
 ))}
 </div>
 )}
 </div>
 </div>
 );
}
